import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { FlaskConical, Loader2, Send, X, Upload, ClipboardPaste, ToggleLeft, ToggleRight } from 'lucide-react';
import StructureImage from '../../components/chemistry/StructureImage';
import { assayRequestApi, AssayRequest, AssayDataPoint } from '../../services/assayRequestApi';
import { useAuth } from '../../context/AuthContext';
import { useUIStore } from '../../stores/uiStore';

// ── Status helpers ───────────────────────────────────────────────

type StatusFilter = 'all' | 'pending' | 'in_progress' | 'completed' | 'rejected';

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  pending:     { bg: 'rgba(156,163,175,0.15)', text: '#6B7280', label: 'Pending' },
  in_progress: { bg: 'rgba(245,158,11,0.15)',  text: '#D97706', label: 'In Progress' },
  completed:   { bg: 'rgba(13,148,136,0.15)',   text: '#0D9488', label: 'Completed' },
  rejected:    { bg: 'rgba(239,68,68,0.15)',     text: '#DC2626', label: 'Rejected' },
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ── Main component ───────────────────────────────────────────────

const AssayQueuePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project') ? parseInt(searchParams.get('project')!, 10) : undefined;
  const { user } = useAuth();
  const showNotification = useUIStore((s) => s.showNotification);

  const [requests, setRequests] = useState<AssayRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  // Data entry panel state
  const [activeRequest, setActiveRequest] = useState<AssayRequest | null>(null);
  const [panelMode, setPanelMode] = useState<'data' | 'view' | null>(null);

  // ── Fetch ────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (projectId) params.project_id = projectId;
      if (statusFilter !== 'all') params.status = statusFilter;
      const { requests: data } = await assayRequestApi.fetchAssayRequests(params);
      setRequests(data);
    } catch (err: any) {
      console.error('Failed to load assay requests:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Counts ───────────────────────────────────────────────
  const [allRequests, setAllRequests] = useState<AssayRequest[]>([]);
  useEffect(() => {
    const params: Record<string, any> = {};
    if (projectId) params.project_id = projectId;
    assayRequestApi.fetchAssayRequests(params).then(r => setAllRequests(r.requests)).catch(() => {});
  }, [projectId]);

  const counts = useMemo(() => {
    const c = { all: allRequests.length, pending: 0, in_progress: 0, completed: 0, rejected: 0 };
    allRequests.forEach(r => { if (r.status in c) (c as any)[r.status]++; });
    return c;
  }, [allRequests]);

  // ── Actions ──────────────────────────────────────────────
  const handlePickUp = async (req: AssayRequest) => {
    try {
      await assayRequestApi.claimAssayRequest(req.id);
      showNotification('success', `Picked up ${req.assay_type_name} request`);
      fetchData();
      // Refresh counts
      const params: Record<string, any> = {};
      if (projectId) params.project_id = projectId;
      assayRequestApi.fetchAssayRequests(params).then(r => setAllRequests(r.requests)).catch(() => {});
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to claim request');
    }
  };

  const handleEnterData = async (req: AssayRequest) => {
    try {
      const full = await assayRequestApi.fetchAssayRequest(req.id);
      setActiveRequest(full);
      setPanelMode('data');
    } catch {
      showNotification('error', 'Failed to load request details');
    }
  };

  const handleViewResults = async (req: AssayRequest) => {
    try {
      const full = await assayRequestApi.fetchAssayRequest(req.id);
      setActiveRequest(full);
      setPanelMode('view');
    } catch {
      showNotification('error', 'Failed to load request details');
    }
  };

  const closePanel = () => {
    setActiveRequest(null);
    setPanelMode(null);
  };

  const onPanelComplete = () => {
    closePanel();
    fetchData();
    const params: Record<string, any> = {};
    if (projectId) params.project_id = projectId;
    assayRequestApi.fetchAssayRequests(params).then(r => setAllRequests(r.requests)).catch(() => {});
  };

  // ── Column Defs ──────────────────────────────────────────
  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: 'Compound',
      field: 'compound_code',
      width: 130,
      cellRenderer: (params: ICellRendererParams) => {
        const code = params.data?.compound_code;
        return code ? (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>{code}</span>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>#{params.data?.molecule_id}</span>
        );
      },
    },
    {
      headerName: 'Structure',
      field: 'molecule_id',
      width: 80,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data?.molecule_id) return null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <StructureImage moleculeId={params.data.molecule_id} size={48} lazy />
          </div>
        );
      },
    },
    {
      headerName: 'Assay Type',
      field: 'assay_type_name',
      width: 110,
      cellRenderer: (params: ICellRendererParams) => (
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 4,
          backgroundColor: 'rgba(13,148,136,0.1)',
          color: '#0D9488',
          fontFamily: 'var(--font-mono)',
        }}>
          {params.value}
        </span>
      ),
    },
    {
      headerName: 'Target',
      field: 'target_name',
      width: 100,
      cellRenderer: (params: ICellRendererParams) => (
        <span style={{ fontSize: 12, fontWeight: 500 }}>{params.value || '—'}</span>
      ),
    },
    {
      headerName: 'Submitted By',
      field: 'submitted_by_name',
      width: 120,
      cellRenderer: (params: ICellRendererParams) => (
        <span style={{ fontSize: 12 }}>{params.value || params.data?.submitted_by || '—'}</span>
      ),
    },
    {
      headerName: 'Date',
      field: 'created_at',
      width: 100,
      cellRenderer: (params: ICellRendererParams) => (
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{timeAgo(params.value)}</span>
      ),
      sort: 'desc' as const,
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 110,
      cellRenderer: (params: ICellRendererParams) => {
        const s = STATUS_COLORS[params.value] || STATUS_COLORS.pending;
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            backgroundColor: s.bg,
            color: s.text,
          }}>
            {s.label}
          </span>
        );
      },
    },
    {
      headerName: 'Action',
      width: 130,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => {
        const req = params.data as AssayRequest;
        if (!req) return null;
        const isMyRequest = req.assigned_to && user?.id && String(req.assigned_to) === String(user.id);

        if (req.status === 'pending') {
          return (
            <button
              onClick={(e) => { e.stopPropagation(); handlePickUp(req); }}
              style={{
                padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                border: 'none', cursor: 'pointer',
                background: '#0D9488', color: 'white',
              }}
            >
              Pick Up
            </button>
          );
        }
        if (req.status === 'in_progress') {
          if (isMyRequest) {
            return (
              <button
                onClick={(e) => { e.stopPropagation(); handleEnterData(req); }}
                style={{
                  padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                  border: 'none', cursor: 'pointer',
                  background: '#0D9488', color: 'white',
                }}
              >
                Enter Data
              </button>
            );
          }
          return (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              {req.assigned_to_name || 'Assigned'}
            </span>
          );
        }
        if (req.status === 'completed') {
          return (
            <button
              onClick={(e) => { e.stopPropagation(); handleViewResults(req); }}
              style={{
                padding: '4px 12px', fontSize: 11, fontWeight: 500, borderRadius: 6,
                border: '1px solid var(--card-border)', cursor: 'pointer',
                background: 'transparent', color: 'var(--text-primary)',
              }}
            >
              View Results
            </button>
          );
        }
        // rejected
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleViewResults(req); }}
            style={{
              padding: '4px 12px', fontSize: 11, fontWeight: 500, borderRadius: 6,
              border: '1px solid var(--card-border)', cursor: 'pointer',
              background: 'transparent', color: 'var(--text-muted)',
            }}
          >
            View
          </button>
        );
      },
    },
  ], [user]);

  const defaultColDef = useMemo<ColDef>(() => ({
    resizable: true,
    sortable: true,
  }), []);

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
  };

  // ── Filter tabs ──────────────────────────────────────────
  const filterTabs: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'completed', label: 'Completed' },
    { key: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="h-full flex flex-col" style={{ color: 'var(--text-primary)' }}>
      {/* Header */}
      <div className="px-6 py-4" style={{
        borderBottom: '1px solid var(--card-border)',
        background: 'var(--card-bg)',
      }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(13,148,136,0.1)' }}>
              <FlaskConical size={18} strokeWidth={1.5} style={{ color: '#0D9488' }} />
            </div>
            <div>
              <h1 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
                Assay Queue
              </h1>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {counts.pending} pending request{counts.pending !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="flex items-center gap-1 mt-3">
          {filterTabs.map(tab => {
            const count = counts[tab.key];
            const active = statusFilter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
                style={{
                  border: 'none',
                  cursor: 'pointer',
                  background: active ? 'linear-gradient(135deg, #0D9488, #14B8A6)' : 'transparent',
                  color: active ? 'white' : 'var(--text-secondary)',
                  transition: 'background-color 0.15s, color 0.15s',
                }}
              >
                {tab.label}
                {count > 0 && (
                  <span style={{
                    padding: '0 6px',
                    borderRadius: 10,
                    fontSize: 10,
                    fontWeight: 700,
                    backgroundColor: active ? 'rgba(255,255,255,0.25)' : 'var(--hover-bg)',
                    color: active ? 'white' : 'var(--text-muted)',
                  }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 flex" style={{ minHeight: 0 }}>
        <div className="flex-1 ag-theme-alpine" style={{ height: '100%' }}>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin" size={20} style={{ color: '#0D9488' }} />
              <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>Loading queue...</span>
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--hover-bg)' }}>
                <FlaskConical size={28} strokeWidth={1.5} style={{ color: 'var(--text-muted)' }} />
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                No {statusFilter !== 'all' ? statusFilter.replace('_', ' ') : ''} requests
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Requests submitted by chemists will appear here
              </p>
            </div>
          ) : (
            <AgGridReact
              rowData={requests}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              rowHeight={56}
              headerHeight={36}
              animateRows={true}
              suppressCellFocus={true}
              getRowId={(params) => String(params.data.id)}
            />
          )}
        </div>

        {/* Side panel */}
        {activeRequest && panelMode && (
          <DataEntryPanel
            request={activeRequest}
            mode={panelMode}
            onClose={closePanel}
            onComplete={onPanelComplete}
          />
        )}
      </div>
    </div>
  );
};

// ── Data Entry Panel ─────────────────────────────────────────────

interface DataEntryPanelProps {
  request: AssayRequest;
  mode: 'data' | 'view';
  onClose: () => void;
  onComplete: () => void;
}

const DataEntryPanel: React.FC<DataEntryPanelProps> = ({ request, mode, onClose, onComplete }) => {
  const showNotification = useUIStore((s) => s.showNotification);

  const inputMode = request.input_mode || 'single_value';

  return (
    <div style={{
      width: 480,
      borderLeft: '1px solid var(--card-border)',
      background: 'var(--card-bg)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Panel header */}
      <div className="px-4 py-3 flex items-center justify-between" style={{
        borderBottom: '1px solid var(--card-border)',
      }}>
        <div>
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {mode === 'view' ? 'Results' : 'Enter Data'} — {request.assay_type_name}
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {request.target_name ? `Target: ${request.target_name}` : ''}
            {request.compound_code ? ` · ${request.compound_code}` : ` · Molecule #${request.molecule_id}`}
          </div>
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
          <X size={18} />
        </button>
      </div>

      {/* Structure preview */}
      <div className="px-4 py-3 flex justify-center" style={{ borderBottom: '1px solid var(--card-border)' }}>
        <StructureImage moleculeId={request.molecule_id} size={120} />
      </div>

      {/* Content based on input mode */}
      <div className="flex-1 overflow-y-auto">
        {mode === 'view' ? (
          <ResultsView request={request} />
        ) : inputMode === 'dose_response' ? (
          <DoseResponsePanel request={request} onComplete={onComplete} />
        ) : inputMode === 'binary' ? (
          <BinaryPanel request={request} onComplete={onComplete} />
        ) : (
          <SingleValuePanel request={request} onComplete={onComplete} />
        )}
      </div>
    </div>
  );
};

// ── Dose-Response Panel ──────────────────────────────────────────

const DoseResponsePanel: React.FC<{ request: AssayRequest; onComplete: () => void }> = ({ request, onComplete }) => {
  const showNotification = useUIStore((s) => s.showNotification);
  const [tab, setTab] = useState<'paste' | 'upload'>('paste');
  const [rawText, setRawText] = useState('');
  const [parsedPoints, setParsedPoints] = useState<AssayDataPoint[]>([]);
  const [dataSubmitted, setDataSubmitted] = useState(!!request.data_points?.length);
  const [fitting, setFitting] = useState(false);
  const [fitResult, setFitResult] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // If request already has data points, show them
  useEffect(() => {
    if (request.data_points?.length) {
      setParsedPoints(request.data_points);
      setDataSubmitted(true);
    }
  }, [request.data_points]);

  const handleParse = async () => {
    if (!rawText.trim()) return;
    try {
      const result = await assayRequestApi.submitDoseResponseData(request.id, {
        raw_text: rawText.trim(),
        concentration_unit: request.typical_units || 'nM',
        response_unit: '% inhibition',
      });
      setParsedPoints(result.data);
      setDataSubmitted(true);
      showNotification('success', `${result.points_saved} data points saved`);
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to parse data');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Read file and extract text
    const text = await file.text();
    setRawText(text);
    try {
      const result = await assayRequestApi.submitDoseResponseData(request.id, {
        raw_text: text,
        concentration_unit: request.typical_units || 'nM',
        response_unit: '% inhibition',
      });
      setParsedPoints(result.data);
      setDataSubmitted(true);
      showNotification('success', `${result.points_saved} data points imported from ${file.name}`);
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to parse file');
    }
  };

  const handleFitCurve = async () => {
    setFitting(true);
    try {
      const result = await assayRequestApi.calculateCurveFit(request.id);
      setFitResult(result);
      if (result.fit?.error) {
        showNotification('warning', `Fit warning: ${result.fit.error}`);
      }
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Curve fitting failed');
    } finally {
      setFitting(false);
    }
  };

  const handleSendBack = async () => {
    setSubmitting(true);
    try {
      await assayRequestApi.approveAssayRequest(request.id);
      showNotification('success', `Results sent to ${request.submitted_by_name || 'submitter'}`);
      onComplete();
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setSubmitting(true);
    try {
      await assayRequestApi.rejectAssayRequest(request.id, rejectReason.trim());
      showNotification('info', 'Request rejected');
      onComplete();
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      {!dataSubmitted ? (
        <>
          {/* Input tabs */}
          <div className="flex gap-1" style={{ borderBottom: '1px solid var(--card-border)', paddingBottom: 8 }}>
            <button
              onClick={() => setTab('paste')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md"
              style={{
                border: 'none', cursor: 'pointer',
                background: tab === 'paste' ? 'rgba(13,148,136,0.1)' : 'transparent',
                color: tab === 'paste' ? '#0D9488' : 'var(--text-muted)',
              }}
            >
              <ClipboardPaste size={14} /> Paste Data
            </button>
            <button
              onClick={() => setTab('upload')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md"
              style={{
                border: 'none', cursor: 'pointer',
                background: tab === 'upload' ? 'rgba(13,148,136,0.1)' : 'transparent',
                color: tab === 'upload' ? '#0D9488' : 'var(--text-muted)',
              }}
            >
              <Upload size={14} /> Upload File
            </button>
          </div>

          {tab === 'paste' ? (
            <>
              <textarea
                value={rawText}
                onChange={e => setRawText(e.target.value)}
                rows={10}
                placeholder={`Concentration\tResponse\n0.1\t98.2\n0.3\t97.5\n1\t95.1\n3\t88.4\n10\t72.3\n30\t48.6\n100\t22.1\n300\t8.4\n1000\t3.2\n3000\t1.8`}
                style={{
                  width: '100%',
                  padding: 12,
                  fontSize: 12,
                  fontFamily: 'var(--font-mono)',
                  borderRadius: 8,
                  border: '1px solid var(--input-border)',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)',
                  resize: 'vertical',
                  outline: 'none',
                }}
              />
              <button
                onClick={handleParse}
                disabled={!rawText.trim()}
                className="px-4 py-2 text-xs font-semibold rounded-lg"
                style={{
                  border: 'none', cursor: rawText.trim() ? 'pointer' : 'not-allowed',
                  background: rawText.trim() ? '#0D9488' : 'var(--hover-bg)',
                  color: rawText.trim() ? 'white' : 'var(--text-muted)',
                  alignSelf: 'flex-start',
                }}
              >
                Parse & Save
              </button>
            </>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed var(--card-border)',
                borderRadius: 12,
                padding: '32px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = '#0D9488')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--card-border)')}
            >
              <Upload size={24} style={{ margin: '0 auto 8px', color: 'var(--text-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Click or drop a CSV / Excel file</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Columns: Concentration, Response</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.tsv,.txt"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </>
      ) : (
        <>
          {/* Preview table */}
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
              Data Points ({parsedPoints.length})
            </div>
            <div style={{
              maxHeight: 200, overflowY: 'auto',
              border: '1px solid var(--card-border)', borderRadius: 8,
            }}>
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--hover-bg)' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Conc.</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Unit</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Response</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Rep.</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedPoints.map((pt, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--card-border)' }}>
                      <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>{pt.concentration}</td>
                      <td style={{ padding: '4px 8px', color: 'var(--text-muted)' }}>{pt.concentration_unit || request.typical_units || 'nM'}</td>
                      <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>{pt.response}</td>
                      <td style={{ padding: '4px 8px', color: 'var(--text-muted)' }}>{pt.replicate || 1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Fit Curve button */}
          {!fitResult && (
            <button
              onClick={handleFitCurve}
              disabled={fitting}
              className="px-4 py-2.5 text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
              style={{
                border: 'none',
                cursor: fitting ? 'wait' : 'pointer',
                background: 'linear-gradient(135deg, #0D9488, #14B8A6)',
                color: 'white',
              }}
            >
              {fitting ? <Loader2 className="animate-spin" size={16} /> : null}
              {fitting ? 'Fitting...' : 'Fit Curve'}
            </button>
          )}

          {/* Curve result */}
          {fitResult && (
            <div>
              {/* Curve image */}
              {fitResult.curve_image_url && (
                <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--card-border)', marginBottom: 12 }}>
                  <img
                    src={fitResult.curve_image_url}
                    alt="4PL Dose-Response Curve"
                    style={{ width: '100%', display: 'block' }}
                  />
                </div>
              )}

              {/* Parameters */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
              }}>
                {[
                  { label: 'IC50', value: `${fitResult.fit.ic50?.toFixed(2)} ${fitResult.unit || request.typical_units || 'nM'}` },
                  { label: 'Hill Slope', value: fitResult.fit.hill_slope?.toFixed(3) },
                  { label: 'R²', value: fitResult.fit.r_squared?.toFixed(4) },
                  { label: 'Top', value: `${fitResult.fit.top?.toFixed(1)}%` },
                  { label: 'Bottom', value: `${fitResult.fit.bottom?.toFixed(1)}%` },
                ].map(p => (
                  <div key={p.label} style={{
                    padding: '8px 10px', borderRadius: 6,
                    background: 'var(--hover-bg)',
                  }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{p.label}</div>
                    <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>{p.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          {(fitResult || request.data_points?.length) && (
            <div className="flex flex-col gap-2 mt-2">
              {!rejectMode ? (
                <div className="flex gap-2">
                  <button
                    onClick={handleSendBack}
                    disabled={submitting}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
                    style={{
                      border: 'none', cursor: submitting ? 'wait' : 'pointer',
                      background: '#0D9488', color: 'white',
                    }}
                  >
                    <Send size={14} /> Send Back
                  </button>
                  <button
                    onClick={() => setRejectMode(true)}
                    className="px-4 py-2.5 text-sm font-semibold rounded-lg"
                    style={{
                      border: '1px solid #DC2626', cursor: 'pointer',
                      background: 'transparent', color: '#DC2626',
                    }}
                  >
                    Reject
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    rows={3}
                    placeholder="Reason for rejection..."
                    style={{
                      width: '100%', padding: 10, fontSize: 12,
                      borderRadius: 8, border: '1px solid var(--input-border)',
                      background: 'var(--input-bg)', color: 'var(--text-primary)',
                      resize: 'none', outline: 'none',
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleReject}
                      disabled={submitting || !rejectReason.trim()}
                      className="flex-1 px-4 py-2 text-xs font-semibold rounded-lg"
                      style={{
                        border: 'none', cursor: submitting || !rejectReason.trim() ? 'not-allowed' : 'pointer',
                        background: '#DC2626', color: 'white',
                        opacity: submitting || !rejectReason.trim() ? 0.5 : 1,
                      }}
                    >
                      Confirm Reject
                    </button>
                    <button
                      onClick={() => { setRejectMode(false); setRejectReason(''); }}
                      className="px-4 py-2 text-xs rounded-lg"
                      style={{
                        border: '1px solid var(--card-border)', cursor: 'pointer',
                        background: 'transparent', color: 'var(--text-secondary)',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Single Value Panel ───────────────────────────────────────────

const SingleValuePanel: React.FC<{ request: AssayRequest; onComplete: () => void }> = ({ request, onComplete }) => {
  const showNotification = useUIStore((s) => s.showNotification);
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState(request.typical_units || 'nM');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleSubmitAndApprove = async () => {
    const num = parseFloat(value);
    if (isNaN(num)) {
      showNotification('error', 'Please enter a valid number');
      return;
    }
    setSubmitting(true);
    try {
      await assayRequestApi.submitSingleValueResult(request.id, { value: num, unit, notes: notes || undefined });
      await assayRequestApi.approveAssayRequest(request.id);
      showNotification('success', `Results sent to ${request.submitted_by_name || 'submitter'}`);
      onComplete();
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setSubmitting(true);
    try {
      await assayRequestApi.rejectAssayRequest(request.id, rejectReason.trim());
      showNotification('info', 'Request rejected');
      onComplete();
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <div>
        <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Value *</label>
        <input
          type="number"
          step="any"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="e.g. 125.5"
          style={{
            width: '100%', padding: '8px 12px', fontSize: 13,
            borderRadius: 8, border: '1px solid var(--input-border)',
            background: 'var(--input-bg)', color: 'var(--text-primary)',
            fontFamily: 'var(--font-mono)', outline: 'none',
          }}
        />
      </div>
      <div>
        <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Unit</label>
        <input
          value={unit}
          onChange={e => setUnit(e.target.value)}
          style={{
            width: '100%', padding: '8px 12px', fontSize: 13,
            borderRadius: 8, border: '1px solid var(--input-border)',
            background: 'var(--input-bg)', color: 'var(--text-primary)',
            outline: 'none',
          }}
        />
      </div>
      <div>
        <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Notes</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Optional notes..."
          style={{
            width: '100%', padding: '8px 12px', fontSize: 13,
            borderRadius: 8, border: '1px solid var(--input-border)',
            background: 'var(--input-bg)', color: 'var(--text-primary)',
            resize: 'none', outline: 'none',
          }}
        />
      </div>

      {!rejectMode ? (
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSubmitAndApprove}
            disabled={submitting || !value}
            className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
            style={{
              border: 'none',
              cursor: submitting || !value ? 'not-allowed' : 'pointer',
              background: '#0D9488', color: 'white',
              opacity: submitting || !value ? 0.5 : 1,
            }}
          >
            <Send size={14} /> Send Back
          </button>
          <button
            onClick={() => setRejectMode(true)}
            className="px-4 py-2.5 text-sm font-semibold rounded-lg"
            style={{
              border: '1px solid #DC2626', cursor: 'pointer',
              background: 'transparent', color: '#DC2626',
            }}
          >
            Reject
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            rows={3}
            placeholder="Reason for rejection..."
            style={{
              width: '100%', padding: 10, fontSize: 12,
              borderRadius: 8, border: '1px solid var(--input-border)',
              background: 'var(--input-bg)', color: 'var(--text-primary)',
              resize: 'none', outline: 'none',
            }}
          />
          <div className="flex gap-2">
            <button
              onClick={handleReject}
              disabled={submitting || !rejectReason.trim()}
              className="flex-1 px-4 py-2 text-xs font-semibold rounded-lg"
              style={{
                border: 'none',
                cursor: submitting || !rejectReason.trim() ? 'not-allowed' : 'pointer',
                background: '#DC2626', color: 'white',
                opacity: submitting || !rejectReason.trim() ? 0.5 : 1,
              }}
            >
              Confirm Reject
            </button>
            <button
              onClick={() => { setRejectMode(false); setRejectReason(''); }}
              className="px-4 py-2 text-xs rounded-lg"
              style={{
                border: '1px solid var(--card-border)', cursor: 'pointer',
                background: 'transparent', color: 'var(--text-secondary)',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Binary Panel ─────────────────────────────────────────────────

const BinaryPanel: React.FC<{ request: AssayRequest; onComplete: () => void }> = ({ request, onComplete }) => {
  const showNotification = useUIStore((s) => s.showNotification);
  const [result, setResult] = useState<boolean | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleSubmitAndApprove = async () => {
    if (result === null) {
      showNotification('error', 'Please select Positive or Negative');
      return;
    }
    setSubmitting(true);
    try {
      await assayRequestApi.submitSingleValueResult(request.id, { binary_result: result, notes: notes || undefined });
      await assayRequestApi.approveAssayRequest(request.id);
      showNotification('success', `Results sent to ${request.submitted_by_name || 'submitter'}`);
      onComplete();
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setSubmitting(true);
    try {
      await assayRequestApi.rejectAssayRequest(request.id, rejectReason.trim());
      showNotification('info', 'Request rejected');
      onComplete();
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <div>
        <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>Result *</label>
        <div className="flex gap-2">
          <button
            onClick={() => setResult(true)}
            className="flex-1 px-4 py-3 text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
            style={{
              border: result === true ? '2px solid #0D9488' : '1px solid var(--card-border)',
              cursor: 'pointer',
              background: result === true ? 'rgba(13,148,136,0.1)' : 'transparent',
              color: result === true ? '#0D9488' : 'var(--text-secondary)',
            }}
          >
            <ToggleRight size={16} /> Positive
          </button>
          <button
            onClick={() => setResult(false)}
            className="flex-1 px-4 py-3 text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
            style={{
              border: result === false ? '2px solid #DC2626' : '1px solid var(--card-border)',
              cursor: 'pointer',
              background: result === false ? 'rgba(220,38,38,0.1)' : 'transparent',
              color: result === false ? '#DC2626' : 'var(--text-secondary)',
            }}
          >
            <ToggleLeft size={16} /> Negative
          </button>
        </div>
      </div>

      <div>
        <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Notes</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="e.g. Strain TA98, +S9 / -S9 metabolic activation"
          style={{
            width: '100%', padding: '8px 12px', fontSize: 13,
            borderRadius: 8, border: '1px solid var(--input-border)',
            background: 'var(--input-bg)', color: 'var(--text-primary)',
            resize: 'none', outline: 'none',
          }}
        />
      </div>

      {!rejectMode ? (
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSubmitAndApprove}
            disabled={submitting || result === null}
            className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
            style={{
              border: 'none',
              cursor: submitting || result === null ? 'not-allowed' : 'pointer',
              background: '#0D9488', color: 'white',
              opacity: submitting || result === null ? 0.5 : 1,
            }}
          >
            <Send size={14} /> Send Back
          </button>
          <button
            onClick={() => setRejectMode(true)}
            className="px-4 py-2.5 text-sm font-semibold rounded-lg"
            style={{
              border: '1px solid #DC2626', cursor: 'pointer',
              background: 'transparent', color: '#DC2626',
            }}
          >
            Reject
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            rows={3}
            placeholder="Reason for rejection..."
            style={{
              width: '100%', padding: 10, fontSize: 12,
              borderRadius: 8, border: '1px solid var(--input-border)',
              background: 'var(--input-bg)', color: 'var(--text-primary)',
              resize: 'none', outline: 'none',
            }}
          />
          <div className="flex gap-2">
            <button
              onClick={handleReject}
              disabled={submitting || !rejectReason.trim()}
              className="flex-1 px-4 py-2 text-xs font-semibold rounded-lg"
              style={{
                border: 'none',
                cursor: submitting || !rejectReason.trim() ? 'not-allowed' : 'pointer',
                background: '#DC2626', color: 'white',
                opacity: submitting || !rejectReason.trim() ? 0.5 : 1,
              }}
            >
              Confirm Reject
            </button>
            <button
              onClick={() => { setRejectMode(false); setRejectReason(''); }}
              className="px-4 py-2 text-xs rounded-lg"
              style={{
                border: '1px solid var(--card-border)', cursor: 'pointer',
                background: 'transparent', color: 'var(--text-secondary)',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Results View (completed/rejected) ────────────────────────────

const ResultsView: React.FC<{ request: AssayRequest }> = ({ request }) => {
  const inputMode = request.input_mode || 'single_value';

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Status badge */}
      <div className="flex items-center gap-2">
        {(() => {
          const s = STATUS_COLORS[request.status] || STATUS_COLORS.pending;
          return (
            <span style={{
              padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
              backgroundColor: s.bg, color: s.text,
            }}>
              {s.label}
            </span>
          );
        })()}
        {request.reject_reason && (
          <span className="text-xs" style={{ color: '#DC2626' }}>
            — {request.reject_reason}
          </span>
        )}
      </div>

      {/* Dose-response results */}
      {inputMode === 'dose_response' && (
        <>
          {/* Curve image */}
          {(request.curve_image_path || request.curve_image_url) && (
            <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--card-border)' }}>
              <img
                src={request.curve_image_url || assayRequestApi.getCurveImageUrl(request.id)}
                alt="4PL Dose-Response Curve"
                style={{ width: '100%', display: 'block' }}
              />
            </div>
          )}

          {/* 4PL parameters */}
          {request.result_value != null && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'IC50', value: `${request.result_value?.toFixed(2)} ${request.result_unit || request.typical_units || 'nM'}` },
                { label: 'Hill Slope', value: request.result_hill_slope?.toFixed(3) || '—' },
                { label: 'R²', value: request.result_r_squared?.toFixed(4) || '—' },
                { label: 'Top', value: request.result_top != null ? `${request.result_top.toFixed(1)}%` : '—' },
                { label: 'Bottom', value: request.result_bottom != null ? `${request.result_bottom.toFixed(1)}%` : '—' },
              ].map(p => (
                <div key={p.label} style={{ padding: '8px 10px', borderRadius: 6, background: 'var(--hover-bg)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{p.label}</div>
                  <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>{p.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Raw data points */}
          {request.data_points && request.data_points.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                Raw Data ({request.data_points.length} points)
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--card-border)', borderRadius: 8 }}>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--hover-bg)' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Conc.</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Response</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Rep.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {request.data_points.map((pt, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--card-border)' }}>
                        <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>{pt.concentration}</td>
                        <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>{pt.response}</td>
                        <td style={{ padding: '4px 8px', color: 'var(--text-muted)' }}>{pt.replicate || 1}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Single value results */}
      {inputMode === 'single_value' && request.result_value != null && (
        <div style={{ padding: 16, borderRadius: 8, background: 'var(--hover-bg)', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-mono)', color: '#0D9488' }}>
            {request.result_value}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {request.result_unit || request.typical_units || ''}
          </div>
        </div>
      )}

      {/* Binary results */}
      {inputMode === 'binary' && request.result_binary != null && (
        <div style={{
          padding: 16, borderRadius: 8, textAlign: 'center',
          background: request.result_binary ? 'rgba(13,148,136,0.1)' : 'rgba(220,38,38,0.1)',
        }}>
          <div style={{
            fontSize: 20, fontWeight: 700,
            color: request.result_binary ? '#0D9488' : '#DC2626',
          }}>
            {request.result_binary ? 'Positive' : 'Negative'}
          </div>
        </div>
      )}

      {/* Notes */}
      {request.notes && (
        <div>
          <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Notes</div>
          <div className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>{request.notes}</div>
        </div>
      )}

      {/* Metadata */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
        borderTop: '1px solid var(--card-border)', paddingTop: 12,
      }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>Submitted By</div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', marginTop: 2 }}>{request.submitted_by_name || request.submitted_by || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>Completed By</div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', marginTop: 2 }}>{request.assigned_to_name || request.assigned_to || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>Submitted</div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', marginTop: 2 }}>{request.created_at ? new Date(request.created_at).toLocaleDateString() : '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>Completed</div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', marginTop: 2 }}>{request.completed_at ? new Date(request.completed_at).toLocaleDateString() : '—'}</div>
        </div>
      </div>
    </div>
  );
};

export default AssayQueuePage;
